/* ========================================================================
   © 2026 FixMySiteKit Framework. All rights reserved.
   AdminOnlyRoute — dual gate: Google OAuth session + isAdmin flag on profile.
   Anon OR non-admin visitors are bounced to `/` instantly. No login screen,
   no error message — the admin engine is invisible to non-admins.
   ======================================================================== */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminOnlyRoute({ children }) {
    const navigate = useNavigate();
    const [phase, setPhase] = useState("checking");

    useEffect(() => {
        (async () => {
            try {
                await api.get("/auth/me", { withCredentials: true });
                const { data: ws } = await api.get("/auth/my-workspace", { withCredentials: true });
                if (ws?.isAdmin) {
                    setPhase("ok");
                } else {
                    navigate("/", { replace: true });
                }
            } catch (_) {
                navigate("/", { replace: true });
            }
        })();
    }, [navigate]);

    // noindex on every admin page — prevents Google/Bing from ever indexing
    useEffect(() => {
        const prev = document.querySelector('meta[name="robots"]');
        const created = !prev;
        const meta = prev || document.createElement("meta");
        meta.setAttribute("name", "robots");
        meta.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
        if (created) document.head.appendChild(meta);
        document.title = "Restricted";
        return () => {
            if (created && meta.parentNode) meta.parentNode.removeChild(meta);
        };
    }, []);

    if (phase !== "ok") {
        return (
            <div className="grid min-h-screen place-items-center bg-background text-foreground">
                <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
            </div>
        );
    }
    return children;
}
